/**
 * MenuManagementPage Component
 * 
 * Allows store admins to manage menu items - add, edit, delete, search, filter by category.
 * Includes bulk operations (upload/download CSV) for menu management.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onBack - Callback to navigate back
 * @param {Object} props.currentStore - Current store data
 * @param {Object} props.currentUser - Current logged-in user
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Upload, Download, Edit, Search, Home, FileText, X, Check, Trash2, Percent, DollarSign, FolderEdit } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { getImageUrl } from '../../utils/formatters';
import { Checkbox } from '../ui/checkbox';

function MenuManagementPage({ onBack, currentStore, currentUser }) {
  // Helper function to get store ID
  const getStoreId = () => {
    return currentUser?.storeId || currentStore?.storeId || currentStore?.id;
  };

  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Function to extract unique UOMs from menu items
  const getUniqueUOMs = () => {
    if (!menuItems || menuItems.length === 0) {
      console.log('getUniqueUOMs: No menu items available', menuItems?.length);
      return [];
    }
    const uoms = menuItems
      .map(item => item.unit_of_measure)
      .filter(uom => uom && uom.trim() !== '');
    const uniqueUOMs = [...new Set(uoms)].sort();
    console.log('getUniqueUOMs: Found UOMs', uniqueUOMs);
    return uniqueUOMs;
  };

  // Function to extract unique sizes from pricing objects, filtered by UOM
  const getSizesByUOM = (selectedUOM) => {
    if (!menuItems || menuItems.length === 0) {
      console.log('getSizesByUOM: No menu items available');
      return [];
    }
    
    // Filter items by UOM
    const filteredItems = menuItems.filter(item => item.unit_of_measure === selectedUOM);
    console.log(`getSizesByUOM: Found ${filteredItems.length} items with UOM '${selectedUOM}'`);
    
    // Extract all pricing keys from filtered items
    const allSizes = new Set();
    filteredItems.forEach(item => {
      if (item.pricing && typeof item.pricing === 'object') {
        Object.keys(item.pricing).forEach(size => allSizes.add(size));
      }
    });
    
    const sizes = Array.from(allSizes).sort();
    console.log(`getSizesByUOM: Found sizes for '${selectedUOM}':`, sizes);
    return sizes;
  };
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState(''); // Actually applied search
  const [editingItem, setEditingItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  
  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    category: '',
    protein_type: '',
    pricing: {}, // Object to store different tray sizes and prices
    unit_of_measure: '',
    notes: '',
    customCategory: '',
    customUOM: ''
  });
  
  const [customSizes, setCustomSizes] = useState([
    { size: '', price: '' }
  ]);

  // Bulk Edit States (Store Owner only)
  const isStoreOwner = currentUser?.role === 'store_owner';
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkPriceType, setBulkPriceType] = useState('percent'); // 'percent' or 'fixed'
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  const [bulkPriceUOM, setBulkPriceUOM] = useState('all'); // Filter by UOM
  const [bulkNewCategory, setBulkNewCategory] = useState('');
  const [bulkCategoryFilter, setBulkCategoryFilter] = useState('all'); // Filter: 'all' or specific category
  const [bulkCategoryInputMode, setBulkCategoryInputMode] = useState('existing'); // 'existing' or 'new'

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const storeId = getStoreId();
      console.log('Menu Management - Store ID:', storeId);
      
      if (!storeId) {
        console.error('No store ID available');
        toast.error('Store information not available');
        return;
      }
      
      const allItems = [];
      const catResponse = await axios.get(`${API}/categories?store_id=${storeId}`);
      console.log('Categories from backend:', catResponse.data);
      
      for (const cat of catResponse.data) {
        const items = await axios.get(`${API}/menu-items?category=${encodeURIComponent(cat)}&store_id=${storeId}`);
        console.log(`Items for category ${cat}:`, items.data.length);
        allItems.push(...items.data);
      }
      console.log('Total menu items loaded (before dedup):', allItems.length);
      
      // Remove duplicates by ID
      const uniqueItems = [];
      const seenIds = new Set();
      for (const item of allItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          uniqueItems.push(item);
        }
      }
      
      console.log('Total menu items after dedup:', uniqueItems.length);
      console.log('Sample item:', uniqueItems[0]);
      setMenuItems(uniqueItems);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const storeId = getStoreId();
      if (!storeId) {
        console.error('No store ID available for categories');
        return;
      }
      const response = await axios.get(`${API}/categories?store_id=${storeId}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!itemForm.name || !itemForm.category) {
      toast.error('Please fill in name and category');
      return;
    }

    try {
      setLoading(true);
      // Use custom category if CUSTOM is selected
      const finalCategory = itemForm.category === 'CUSTOM' && itemForm.customCategory 
        ? itemForm.customCategory 
        : itemForm.category;
      
      // Use custom UOM if Custom is selected
      const finalUOM = itemForm.unit_of_measure === 'Custom' && itemForm.customUOM
        ? itemForm.customUOM
        : itemForm.unit_of_measure;
      
      // Build pricing object based on UOM type
      let finalPricing = {};
      
      // Check if this is a custom UOM (either "Custom" for new, or existing custom UOMs)
      const isCustomUOM = itemForm.unit_of_measure === 'Custom' || 
                          (itemForm.unit_of_measure && 
                           !itemForm.unit_of_measure.toLowerCase().includes('tray') && 
                           !itemForm.unit_of_measure.toLowerCase().includes('piece') &&
                           customSizes.length > 0 && customSizes[0].size !== '');
      
      if (isCustomUOM) {
        // For custom UOM, build pricing from customSizes
        const validSizes = customSizes.filter(s => s.size && s.price);
        if (validSizes.length === 0) {
          toast.error('Please add at least one size with price for custom UOM');
          setLoading(false);
          return;
        }
        
        validSizes.forEach(sizeItem => {
          const sizeKey = sizeItem.size.toLowerCase().replace(/\s+/g, '_');
          finalPricing[sizeKey] = parseFloat(sizeItem.price);
        });
      } else {
        // For standard UOMs, use the existing pricing from itemForm
        finalPricing = itemForm.pricing || {};
      }
      
      const itemData = {
        name: itemForm.name,
        category: finalCategory,
        protein_type: itemForm.protein_type || '',
        pricing: finalPricing,
        unit_of_measure: finalUOM || '',
        notes: itemForm.notes || '',
        storeId: getStoreId()  // Always include storeId for both create and update
      };

      if (editingItem) {
        // For updates, also include the item's ID
        itemData.id = editingItem.id;
        await axios.put(`${API}/menu-items/${editingItem.id}`, itemData, {
          headers: {
            'X-User-Role': currentUser.role
          }
        });
        toast.success('Menu item updated!');
      } else {
        console.log('Creating menu item with data:', JSON.stringify(itemData, null, 2));
        console.log('Store ID:', getStoreId());
        console.log('User Role:', currentUser.role);
        const response = await axios.post(`${API}/menu-items?store_id=${getStoreId()}`, itemData, {
          headers: {
            'X-User-Role': currentUser.role
          }
        });
        console.log('Menu item created successfully:', response.data);
        toast.success('Menu item created!');
      }

      await fetchMenuItems();
      await fetchCategories();
      resetForm();
      setShowItemModal(false);
    } catch (error) {
      console.error('Failed to save menu item:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Failed to save menu item';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save menu item. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/menu-items/${itemId}`, {
        headers: {
          'X-User-Role': currentUser.role,
        },
      });
      toast.success('Menu item deleted');
      await fetchMenuItems();
      await fetchCategories();
    } catch (error) {
      console.error('Failed to delete:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        toast.error(`Failed to delete: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to delete menu item');
      }
    }
  };

  const handleCopy = (item) => {
    setItemForm({
      name: `${item.name} (Copy)`,
      category: item.category,
      protein_type: item.protein_type || '',
      pricing: item.pricing || {},
      unit_of_measure: item.unit_of_measure || '',
      notes: item.notes || '',
      customCategory: '',
      customUOM: ''
    });
    setEditingItem(null);
    setShowItemModal(true);
  };

  const handleEdit = (item) => {
    setItemForm({
      name: item.name,
      category: item.category,
      protein_type: item.protein_type || '',
      pricing: item.pricing || {},
      unit_of_measure: item.unit_of_measure || '',
      notes: item.notes || '',
      customCategory: '',
      customUOM: ''
    });
    
    // If item has a custom UOM (not standard ones), populate customSizes from pricing
    const isCustomUOM = item.unit_of_measure && 
                        !item.unit_of_measure.toLowerCase().includes('tray') &&
                        !item.unit_of_measure.toLowerCase().includes('piece');
    
    if (isCustomUOM && item.pricing && Object.keys(item.pricing).length > 0) {
      const sizes = Object.entries(item.pricing).map(([size, price]) => ({
        size: size.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        price: price.toString()
      }));
      setCustomSizes(sizes.length > 0 ? sizes : [{ size: '', price: '' }]);
    } else {
      setCustomSizes([{ size: '', price: '' }]);
    }
    
    setEditingItem(item);
    setShowItemModal(true);
  };

  const resetForm = () => {
    setItemForm({
      name: '',
      category: '',
      protein_type: '',
      pricing: {},
      unit_of_measure: '',
      notes: '',
      customCategory: '',
      customUOM: ''
    });
    setCustomSizes([{ size: '', price: '' }]);
    setEditingItem(null);
  };

  const getFilteredItems = () => {
    let filtered = menuItems;
    
    console.log('Filtering - Total items:', filtered.length);
    console.log('Selected category:', selectedCategory);
    console.log('Active search query:', activeSearchQuery);
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(i => i.category === selectedCategory);
      console.log('After category filter:', filtered.length, 'items');
    }
    
    if (activeSearchQuery) {
      const query = activeSearchQuery.toLowerCase();
      filtered = filtered.filter(i => 
        i.name.toLowerCase().includes(query)
      );
      console.log('After search filter:', filtered.length, 'items with query:', activeSearchQuery);
    }
    
    return filtered;
  };

  const handleExportMenu = async () => {
    try {
      setLoading(true);
      const storeId = getStoreId();
      if (!storeId) {
        toast.error('Store information not available');
        return;
      }
      const response = await axios.get(`${API}/menu/export?store_id=${storeId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'menu_export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Menu exported successfully');
    } catch (error) {
      console.error('Failed to export menu:', error);
      toast.error('Failed to export menu');
    } finally {
      setLoading(false);
    }
  };

  const handleImportMenuFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const storeId = getStoreId();
      if (!storeId) {
        toast.error('Store information not available');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/menu/import-preview?store_id=${storeId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Show summary preview in a dialog or toast for now
      const { to_update, to_insert, invalid_rows } = response.data;
      let summary = `Preview:\nUpdates: ${to_update.length}\nInserts: ${to_insert.length}`;
      if (invalid_rows && invalid_rows.length > 0) {
        summary += `\nInvalid rows: ${invalid_rows.length}`;
      }
      if (window.confirm(summary + '\n\nProceed with import?')) {
        await axios.post(`${API}/menu/import-apply?store_id=${storeId}`, {
          to_update,
          to_insert,
        });
        toast.success('Menu import completed');
        await fetchMenuItems();
        await fetchCategories();
      } else {
        toast.info('Import cancelled');
      }
    } catch (error) {
      console.error('Failed to import menu:', error);
      toast.error('Failed to import menu');
    } finally {
      setLoading(false);
      // Reset file input so same file can be reselected
      if (e.target) {
        e.target.value = '';
      }
    }
  };


  const handleSearch = () => {
    console.log('Search clicked with query:', searchQuery);
    setActiveSearchQuery(searchQuery);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      console.log('Enter pressed with query:', searchQuery);
      handleSearch();
    }
  };

  // ==================== BULK EDIT FUNCTIONS ====================
  
  // Toggle select all items
  const handleSelectAll = () => {
    const filteredItems = getFilteredItems();
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  // Toggle single item selection
  const handleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Exit bulk edit mode
  const exitBulkEditMode = () => {
    setBulkEditMode(false);
    setSelectedItems(new Set());
  };

  // Get selected items filtered by UOM (for price updates)
  const getSelectedItemsByUOM = (uomFilter) => {
    return menuItems.filter(item => {
      if (!selectedItems.has(item.id)) return false;
      if (uomFilter === 'all') return true;
      return item.unit_of_measure?.toLowerCase().includes(uomFilter.toLowerCase());
    });
  };

  // Bulk update prices
  const handleBulkPriceUpdate = async () => {
    const value = parseFloat(bulkPriceValue);
    if (isNaN(value) || value === 0) {
      toast.error('Please enter a valid price value');
      return;
    }

    const itemsToUpdate = getSelectedItemsByUOM(bulkPriceUOM);
    if (itemsToUpdate.length === 0) {
      toast.error('No items match the selected UOM filter');
      return;
    }

    try {
      setLoading(true);
      
      // Get selected items filtered by UOM
      const itemsToUpdate = getSelectedItemsByUOM(bulkPriceUOM);
      const itemIds = itemsToUpdate.map(item => item.id);
      
      if (itemIds.length === 0) {
        toast.error('No items match the selected UOM filter');
        setLoading(false);
        return;
      }

      // Use dedicated bulk update endpoint
      const response = await axios.post(`${API}/menu-items/bulk-price-update`, {
        item_ids: itemIds,
        update_type: bulkPriceType,
        value: value,
        uom_filter: bulkPriceUOM
      }, {
        headers: {
          'X-User-Role': currentUser?.role || 'store_owner'
        }
      });

      toast.success(`Updated prices for ${response.data.updated_count} items`);
      setShowBulkPriceModal(false);
      setBulkPriceValue('');
      setBulkPriceUOM('all');
      await fetchMenuItems();
    } catch (error) {
      console.error('Bulk price update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update prices');
    } finally {
      setLoading(false);
    }
  };

  // Bulk update category
  const handleBulkCategoryUpdate = async () => {
    if (!bulkNewCategory.trim()) {
      toast.error('Please enter or select a category');
      return;
    }

    // Filter items based on category filter
    let itemsToUpdate = menuItems.filter(item => selectedItems.has(item.id));
    
    if (bulkCategoryFilter !== 'all') {
      itemsToUpdate = itemsToUpdate.filter(item => item.category === bulkCategoryFilter);
    }
    
    if (itemsToUpdate.length === 0) {
      toast.error(bulkCategoryFilter === 'all' 
        ? 'No items selected' 
        : `No selected items found in category "${bulkCategoryFilter}"`);
      return;
    }

    try {
      setLoading(true);
      const itemIds = itemsToUpdate.map(item => item.id);

      // Use dedicated bulk update endpoint
      const response = await axios.post(`${API}/menu-items/bulk-category-update`, {
        item_ids: itemIds,
        new_category: bulkNewCategory.trim()
      }, {
        headers: {
          'X-User-Role': currentUser?.role || 'store_owner'
        }
      });

      toast.success(`Updated category for ${response.data.updated_count} items`);
      setShowBulkCategoryModal(false);
      setBulkNewCategory('');
      setBulkCategoryFilter('all');
      setBulkCategoryInputMode('existing');
      await fetchMenuItems();
      await fetchCategories();
    } catch (error) {
      console.error('Bulk category update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from selected items (for filtering)
  const getSelectedItemsCategories = () => {
    const selectedCats = new Set();
    menuItems.forEach(item => {
      if (selectedItems.has(item.id) && item.category) {
        selectedCats.add(item.category);
      }
    });
    return Array.from(selectedCats).sort();
  };

  // Get count of selected items by category filter
  const getSelectedItemsCountByCategory = (categoryFilter) => {
    if (categoryFilter === 'all') {
      return selectedItems.size;
    }
    return menuItems.filter(item => 
      selectedItems.has(item.id) && item.category === categoryFilter
    ).length;
  };

  // Bulk delete items
  const handleBulkDelete = async () => {
    const itemsToDelete = menuItems.filter(item => selectedItems.has(item.id));
    if (itemsToDelete.length === 0) {
      toast.error('No items selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${itemsToDelete.length} items? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const itemIds = itemsToDelete.map(item => item.id);

      // Use dedicated bulk delete endpoint
      const response = await axios.post(`${API}/menu-items/bulk-delete`, {
        item_ids: itemIds
      }, {
        headers: {
          'X-User-Role': currentUser?.role || 'store_owner'
        }
      });

      toast.success(`Deleted ${response.data.deleted_count} items`);
      setSelectedItems(new Set());
      await fetchMenuItems();
      await fetchCategories();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete items');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      {/* Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <button onClick={onBack} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Back to Dashboard
        </button>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">Menu Management</h1>
          <p className="page-subtitle">Manage menu items and categories</p>
        </div>

        <div className="max-w-7xl mx-auto space-y-6">
          <div className="card-bawarchi">
            <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-6)' }}>
              <h2 className="card-bawarchi-title" style={{ marginBottom: 0 }}>Menu Items ({getFilteredItems().length})</h2>
              <div className="flex flex-wrap gap-2">
                {/* Bulk Edit Button - Store Owner Only */}
                {isStoreOwner && (
                  <button
                    onClick={() => {
                      if (bulkEditMode) {
                        exitBulkEditMode();
                      } else {
                        setBulkEditMode(true);
                      }
                    }}
                    className={`flex items-center gap-2 ${bulkEditMode ? 'btn-bawarchi-primary' : 'btn-bawarchi-secondary'}`}
                    disabled={loading}
                  >
                    <Check className="w-4 h-4" />
                    {bulkEditMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
                  </button>
                )}
                <button
                  onClick={() => { resetForm(); setShowItemModal(true); }}
                  className="btn-bawarchi-primary flex items-center gap-2"
                  disabled={loading || bulkEditMode}
                >
                  <Plus className="w-4 h-4" />
                  Add New Item
                </button>
                <button
                  onClick={handleExportMenu}
                  className="btn-bawarchi-secondary flex items-center gap-2"
                  disabled={loading}
                >
                  <Download className="w-4 h-4" />
                  Export Menu (Excel)
                </button>
                <button
                  onClick={() => document.getElementById('menu-import-input')?.click()}
                  className="btn-bawarchi-secondary flex items-center gap-2"
                  disabled={loading || bulkEditMode}
                >
                  <Upload className="w-4 h-4" />
                  Import Menu (Excel)
                </button>
                <input
                  id="menu-import-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleImportMenuFile}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex gap-4">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  placeholder="Search items by name..."
                  className="flex-1"
                />
                <Button onClick={handleSearch} className="bg-orange-600 hover:bg-orange-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                {activeSearchQuery && (
                  <Button 
                    onClick={() => { 
                      setSearchQuery(''); 
                      setActiveSearchQuery(''); 
                    }} 
                    variant="outline"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {/* Checkbox column for bulk edit mode */}
                        {bulkEditMode && (
                          <th className="text-center p-2 w-10">
                            <Checkbox
                              checked={selectedItems.size === getFilteredItems().length && getFilteredItems().length > 0}
                              onCheckedChange={handleSelectAll}
                              aria-label="Select all"
                            />
                          </th>
                        )}
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Protein Type</th>
                        <th className="text-left p-2">Pricing</th>
                        <th className="text-left p-2">Unit</th>
                        <th className="text-center p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredItems().map((item, index) => (
                        <tr 
                          key={`${item.id}-${index}`} 
                          className={`border-b hover:bg-orange-50 ${selectedItems.has(item.id) ? 'bg-orange-100' : ''}`}
                        >
                          {/* Checkbox for bulk edit mode */}
                          {bulkEditMode && (
                            <td className="text-center p-2">
                              <Checkbox
                                checked={selectedItems.has(item.id)}
                                onCheckedChange={() => handleSelectItem(item.id)}
                                aria-label={`Select ${item.name}`}
                              />
                            </td>
                          )}
                          <td className="p-2">{item.name}</td>
                          <td className="p-2">{item.category}</td>
                          <td className="p-2">{item.protein_type || 'N/A'}</td>
                          <td className="p-2">
                            {item.pricing && typeof item.pricing === 'object' ? (
                              <div className="text-xs space-y-1">
                                {Object.entries(item.pricing).map(([size, price]) => (
                                  <div key={size}>
                                    <span className="font-medium">{size.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span> ${typeof price === 'number' ? price.toFixed(2) : price}
                                  </div>
                                ))}
                              </div>
                            ) : 'N/A'}
                          </td>
                          <td className="p-2">{item.unit_of_measure || 'N/A'}</td>
                          <td className="p-2">
                            <div className="flex gap-1 justify-center">
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} title="Edit" disabled={bulkEditMode}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleCopy(item)} title="Copy" disabled={bulkEditMode}>
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id, item.name)} className="text-red-600" title="Delete" disabled={bulkEditMode}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Powered by Caterlog Footer */}
        <div className="text-center py-6 text-sm text-gray-500">
          Powered by <span className="font-semibold text-orange-600">Caterlog</span>
        </div>
      </div>

      {/* Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Item Name *</Label>
                  <Input value={itemForm.name} onChange={(e) => setItemForm({...itemForm, name: e.target.value})} />
                  {!editingItem && itemForm.name && itemForm.name.length >= 3 && (() => {
                    const similarItems = menuItems.filter(item => 
                      item.name.toLowerCase().includes(itemForm.name.toLowerCase()) ||
                      itemForm.name.toLowerCase().includes(item.name.toLowerCase())
                    ).slice(0, 5);
                    
                    return similarItems.length > 0 ? (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                        <p className="font-semibold text-yellow-800 mb-1">⚠️ Similar items found:</p>
                        <ul className="space-y-1">
                          {similarItems.map(item => (
                            <li key={item.id} className="text-yellow-700">
                              • {item.name} ({item.category}) - {item.unit_of_measure || 'N/A'}
                            </li>
                          ))}
                        </ul>
                        <p className="text-yellow-600 mt-1 italic">Consider using an existing item or edit it instead to avoid duplicates</p>
                      </div>
                    ) : null;
                  })()}
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={itemForm.category} onValueChange={(value) => setItemForm({...itemForm, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.length > 0 ? (
                        categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))
                      ) : (
                        <SelectItem value="CUSTOM" disabled>Loading categories...</SelectItem>
                      )}
                      <SelectItem value="CUSTOM">CUSTOM (New Category)</SelectItem>
                    </SelectContent>
                  </Select>
                  {itemForm.category === 'CUSTOM' && (
                    <Input 
                      className="mt-2"
                      placeholder="Enter new category name" 
                      value={itemForm.customCategory || ''} 
                      onChange={(e) => setItemForm({...itemForm, customCategory: e.target.value})} 
                    />
                  )}
                </div>
                <div>
                  <Label>Protein Type</Label>
                  <Input value={itemForm.protein_type} onChange={(e) => setItemForm({...itemForm, protein_type: e.target.value})} placeholder="e.g., Chicken, Veg, Goat" />
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <Select value={itemForm.unit_of_measure} onValueChange={(value) => setItemForm({...itemForm, unit_of_measure: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select UOM" />
                    </SelectTrigger>
                    <SelectContent>
                      {getUniqueUOMs().length > 0 ? (
                        getUniqueUOMs().map(uom => (
                          <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                        ))
                      ) : null}
                      <SelectItem value="Custom">Custom (New UOM)</SelectItem>
                    </SelectContent>
                  </Select>
                  {itemForm.unit_of_measure === 'Custom' && (
                    <Input 
                      className="mt-2"
                      placeholder="Enter custom unit" 
                      value={itemForm.customUOM || ''} 
                      onChange={(e) => setItemForm({...itemForm, customUOM: e.target.value})} 
                    />
                  )}
                </div>
              </div>
              
              {/* Pricing Section - Show Custom Sizes Builder for Custom UOM */}
              {(itemForm.unit_of_measure === 'Custom' || 
                (itemForm.unit_of_measure && 
                 !itemForm.unit_of_measure.toLowerCase().includes('tray') && 
                 !itemForm.unit_of_measure.toLowerCase().includes('piece') &&
                 customSizes.length > 0 && customSizes[0].size !== '')) ? (
                <div className="space-y-3 border border-orange-200 bg-orange-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-orange-900">Define Sizes & Prices *</Label>
                  </div>
                  
                  {customSizes.map((sizeItem, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border border-orange-200">
                      <Input
                        placeholder="Size name (e.g., Small, Medium)"
                        value={sizeItem.size}
                        onChange={(e) => {
                          const newSizes = [...customSizes];
                          newSizes[index].size = e.target.value;
                          setCustomSizes(newSizes);
                        }}
                        className="flex-1"
                      />
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600">$</span>
                        <Input
                          type="number"
                          placeholder="Price"
                          value={sizeItem.price}
                          onChange={(e) => {
                            const newSizes = [...customSizes];
                            newSizes[index].price = e.target.value;
                            setCustomSizes(newSizes);
                          }}
                          className="pl-7"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      {customSizes.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCustomSizes(customSizes.filter((_, i) => i !== index));
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCustomSizes([...customSizes, { size: '', price: '' }])}
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Size
                  </Button>
                  
                  <div className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                    <strong>Note:</strong> All sizes will be saved to the menu.
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Pricing {itemForm.unit_of_measure && `(by ${itemForm.unit_of_measure})`}</Label>
                  
                  {/* Per Tray pricing */}
                  {itemForm.unit_of_measure?.toLowerCase().includes('tray') ? (
                    <div className="space-y-2 mt-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-600">QT (Quarter Tray)</Label>
                          <Input 
                            placeholder="Quarter Tray" 
                            type="number" 
                            step="0.01"
                            value={itemForm.pricing?.qt || ''} 
                            onChange={(e) => setItemForm({...itemForm, pricing: {...itemForm.pricing, qt: parseFloat(e.target.value) || 0}})} 
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">MHT (Medium Half Tray)</Label>
                          <Input 
                            placeholder="Medium Half Tray" 
                            type="number" 
                            step="0.01"
                            value={itemForm.pricing?.mht || ''} 
                            onChange={(e) => setItemForm({...itemForm, pricing: {...itemForm.pricing, mht: parseFloat(e.target.value) || 0}})} 
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-gray-600">HT (Half Tray)</Label>
                          <Input 
                            placeholder="Half Tray" 
                            type="number" 
                            step="0.01"
                            value={itemForm.pricing?.ht || ''} 
                            onChange={(e) => setItemForm({...itemForm, pricing: {...itemForm.pricing, ht: parseFloat(e.target.value) || 0}})} 
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">MLT (Medium Large Tray)</Label>
                          <Input 
                            placeholder="Med Large Tray" 
                            type="number" 
                            step="0.01"
                            value={itemForm.pricing?.mlt || ''} 
                            onChange={(e) => setItemForm({...itemForm, pricing: {...itemForm.pricing, mlt: parseFloat(e.target.value) || 0}})} 
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">LT (Large Tray)</Label>
                          <Input 
                            placeholder="Large Tray" 
                            type="number" 
                            step="0.01"
                            value={itemForm.pricing?.lt || ''} 
                            onChange={(e) => setItemForm({...itemForm, pricing: {...itemForm.pricing, lt: parseFloat(e.target.value) || 0}})} 
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 italic">Note: Database uses keys: qt, mht, ht, mlt, lt</p>
                    </div>
                  ) : (
                    /* Per Piece / Per Order pricing */
                    <div className="space-y-2 mt-2">
                      <Label className="text-xs text-gray-600">Base Price (Per {itemForm.unit_of_measure?.replace('Per ', '') || 'Unit'})</Label>
                      <Input 
                        placeholder="Enter price" 
                        type="number" 
                        step="0.01"
                        value={itemForm.pricing?.per_piece || ''} 
                        onChange={(e) => setItemForm({...itemForm, pricing: {per_piece: parseFloat(e.target.value) || 0}})} 
                      />
                      <p className="text-xs text-gray-500 italic mt-1">Note: Database uses key: per_piece</p>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Input value={itemForm.notes} onChange={(e) => setItemForm({...itemForm, notes: e.target.value})} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowItemModal(false); resetForm(); }}>Cancel</Button>
                <Button onClick={handleCreateOrUpdate} className="bg-orange-600 hover:bg-orange-700">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Change Password Modal - Temporarily Hidden
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          {currentUser && (
            <StoreOwnerPasswordChangeForm
              currentUser={currentUser}
              onClose={() => setShowChangePasswordModal(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      */}

      {/* ==================== BULK EDIT FLOATING ACTION BAR ==================== */}
      {bulkEditMode && selectedItems.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-2xl border-2 border-orange-500 p-4 z-50 flex items-center gap-4">
          <div className="flex items-center gap-2 pr-4 border-r border-gray-300">
            <Check className="w-5 h-5 text-orange-600" />
            <span className="font-semibold text-gray-800">{selectedItems.size} items selected</span>
          </div>
          
          <Button 
            onClick={() => setShowBulkPriceModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            Update Prices
          </Button>
          
          <Button 
            onClick={() => setShowBulkCategoryModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
          >
            <FolderEdit className="w-4 h-4" />
            Change Category
          </Button>
          
          <Button 
            onClick={handleBulkDelete}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </Button>
          
          <Button 
            onClick={() => setSelectedItems(new Set())}
            variant="outline"
            className="ml-2"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* ==================== BULK PRICE UPDATE MODAL ==================== */}
      <Dialog open={showBulkPriceModal} onOpenChange={setShowBulkPriceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Bulk Update Prices
            </DialogTitle>
            <DialogDescription>
              Update prices for {selectedItems.size} selected items
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Update Type */}
            <div className="space-y-2">
              <Label>Update Type</Label>
              <div className="flex gap-2">
                <Button
                  variant={bulkPriceType === 'percent' ? 'default' : 'outline'}
                  onClick={() => setBulkPriceType('percent')}
                  className={bulkPriceType === 'percent' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                >
                  <Percent className="w-4 h-4 mr-2" />
                  Percentage
                </Button>
                <Button
                  variant={bulkPriceType === 'fixed' ? 'default' : 'outline'}
                  onClick={() => setBulkPriceType('fixed')}
                  className={bulkPriceType === 'fixed' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Fixed Amount
                </Button>
              </div>
            </div>

            {/* UOM Filter */}
            <div className="space-y-2">
              <Label>Apply to UOM</Label>
              <Select value={bulkPriceUOM} onValueChange={setBulkPriceUOM}>
                <SelectTrigger>
                  <SelectValue placeholder="Select UOM filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All UOMs</SelectItem>
                  <SelectItem value="tray">Per Tray items only</SelectItem>
                  <SelectItem value="piece">Per Piece items only</SelectItem>
                  <SelectItem value="order">Per Order items only</SelectItem>
                  {getUniqueUOMs().filter(uom => 
                    !uom.toLowerCase().includes('tray') && 
                    !uom.toLowerCase().includes('piece') &&
                    !uom.toLowerCase().includes('order')
                  ).map(uom => (
                    <SelectItem key={uom} value={uom}>{uom} items only</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {bulkPriceUOM === 'all' 
                  ? `Will update all ${selectedItems.size} selected items`
                  : `Will update ${getSelectedItemsByUOM(bulkPriceUOM).length} items matching "${bulkPriceUOM}"`
                }
              </p>
            </div>

            {/* Value Input */}
            <div className="space-y-2">
              <Label>
                {bulkPriceType === 'percent' ? 'Percentage Change (%)' : 'Amount Change ($)'}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step={bulkPriceType === 'percent' ? '1' : '0.01'}
                  value={bulkPriceValue}
                  onChange={(e) => setBulkPriceValue(e.target.value)}
                  placeholder={bulkPriceType === 'percent' ? 'e.g., 10 for +10%' : 'e.g., 5 for +$5'}
                  className="flex-1"
                />
                <span className="text-gray-500 font-medium">
                  {bulkPriceType === 'percent' ? '%' : '$'}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Use positive numbers to increase, negative to decrease (e.g., -10 for 10% decrease)
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowBulkPriceModal(false);
              setBulkPriceValue('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkPriceUpdate}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!bulkPriceValue || loading}
            >
              {loading ? 'Updating...' : 'Update Prices'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== BULK CATEGORY CHANGE MODAL ==================== */}
      <Dialog open={showBulkCategoryModal} onOpenChange={setShowBulkCategoryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderEdit className="w-5 h-5 text-purple-600" />
              Change Category
            </DialogTitle>
            <DialogDescription>
              Move selected items to a different category
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Filter by Current Category */}
            <div className="space-y-2">
              <Label>Apply to items from</Label>
              <Select value={bulkCategoryFilter} onValueChange={setBulkCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select which items to update" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value="all">All selected items ({selectedItems.size})</SelectItem>
                  {getSelectedItemsCategories().map(cat => (
                    <SelectItem key={cat} value={cat}>
                      Only "{cat}" items ({getSelectedItemsCountByCategory(cat)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {bulkCategoryFilter === 'all' 
                  ? `Will update all ${selectedItems.size} selected items`
                  : `Will update ${getSelectedItemsCountByCategory(bulkCategoryFilter)} items from "${bulkCategoryFilter}"`
                }
              </p>
            </div>

            {/* Category Input Mode Toggle */}
            <div className="space-y-2">
              <Label>New Category</Label>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  variant={bulkCategoryInputMode === 'existing' ? 'default' : 'outline'}
                  onClick={() => {
                    setBulkCategoryInputMode('existing');
                    setBulkNewCategory('');
                  }}
                  className={bulkCategoryInputMode === 'existing' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  size="sm"
                >
                  Select Existing
                </Button>
                <Button
                  type="button"
                  variant={bulkCategoryInputMode === 'new' ? 'default' : 'outline'}
                  onClick={() => {
                    setBulkCategoryInputMode('new');
                    setBulkNewCategory('');
                  }}
                  className={bulkCategoryInputMode === 'new' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  size="sm"
                >
                  Create New
                </Button>
              </div>
              
              {bulkCategoryInputMode === 'existing' ? (
                <Select value={bulkNewCategory} onValueChange={setBulkNewCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select existing category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Enter new category name"
                  value={bulkNewCategory}
                  onChange={(e) => setBulkNewCategory(e.target.value)}
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowBulkCategoryModal(false);
              setBulkNewCategory('');
              setBulkCategoryFilter('all');
              setBulkCategoryInputMode('existing');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkCategoryUpdate}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={!bulkNewCategory.trim() || loading}
            >
              {loading ? 'Updating...' : `Change Category (${getSelectedItemsCountByCategory(bulkCategoryFilter)} items)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// Menu Item Size Selector Component for Cart Review

export default MenuManagementPage;
